import { Decimal } from "@station/core";

/**
 * Tanklar ekranının SAF hesapları: doluluk oranları (yalnızca SVG çizimi için sayıya çevrilir),
 * seviye bayrakları, okuma tazeliği ve "aralık → çözünürlük" seçimi. Hiçbiri React ya da i18n bilmez;
 * metinler çağıran bileşende üretilir (AGENTS.md §3.9).
 */

// ── Doluluk göstergesi ───────────────────────────────────────────────────────
export interface TankGaugeInput {
  nominalCapacityL: string;
  safeFillVolumeL: string;
  minLevelL: string;
  criticalLevelL: string;
  lastReading: { grossVolumeL: string; levelMm: string | null; waterMm: string | null } | null;
}

export interface TankGaugeGeometry {
  /** Brüt hacim / nominal kapasite (0…1). ÇİZİM içindir; hesap `Decimal` ile yapılır. */
  fillRatio: number;
  /** Dipteki su bandının tank yüksekliğine oranı (0…1); su ölçülmemişse 0. */
  waterRatio: number;
  safeFillRatio: number;
  minLevelRatio: number;
  criticalLevelRatio: number;
}

const clamp01 = (value: Decimal): number => {
  const asNumber = Number(value.toString());
  if (!Number.isFinite(asNumber)) return 0;
  return Math.min(1, Math.max(0, asNumber));
};

function ratio(value: string, capacity: Decimal): number {
  if (capacity.isZero() || capacity.isNegative()) return 0;
  return clamp01(Decimal.from(value).div(capacity));
}

/**
 * Tank görselinin oranları. Su seviyesi tankın yüksekliğini bilmediğimiz için ölçülen SIVI seviyesine
 * (mm) göre paylaştırılır: su bandı, doluluğun su/seviye kadarlık kısmıdır. Yükseklik verisi gelmeden
 * daha kesin bir yaklaşım mümkün değildir; bant zaten METİNLE de ("su 3 mm") desteklenir.
 */
export function tankGaugeGeometry(tank: TankGaugeInput): TankGaugeGeometry {
  const capacity = Decimal.from(tank.nominalCapacityL);
  const fillRatio = tank.lastReading ? ratio(tank.lastReading.grossVolumeL, capacity) : 0;
  const level = tank.lastReading?.levelMm ?? null;
  const water = tank.lastReading?.waterMm ?? null;
  let waterRatio = 0;
  if (level !== null && water !== null) {
    const levelDecimal = Decimal.from(level);
    if (levelDecimal.isPositive())
      waterRatio = Math.min(fillRatio, clamp01(Decimal.from(water).div(levelDecimal)) * fillRatio);
  }
  return {
    fillRatio,
    waterRatio,
    safeFillRatio: ratio(tank.safeFillVolumeL, capacity),
    minLevelRatio: ratio(tank.minLevelL, capacity),
    criticalLevelRatio: ratio(tank.criticalLevelL, capacity),
  };
}

// ── Seviye bayrakları ────────────────────────────────────────────────────────
export const TANK_LEVEL_FLAGS = ["CRITICAL", "BELOW_MIN", "ABOVE_SAFE_FILL"] as const;
export type TankLevelFlag = (typeof TANK_LEVEL_FLAGS)[number];

/**
 * Kritik seviye zaten minimumun altındadır: ikisi birden gösterilmez, en ağır olan yazılır.
 * Güvenli üst sınır aşımı ayrı bir uyarıdır (taşma riski) ve alt seviye uyarılarıyla birlikte gelemez.
 */
export function tankLevelFlags(tank: {
  belowCriticalLevel: boolean;
  belowMinLevel: boolean;
  aboveSafeFill: boolean;
}): TankLevelFlag[] {
  const flags: TankLevelFlag[] = [];
  if (tank.belowCriticalLevel) flags.push("CRITICAL");
  else if (tank.belowMinLevel) flags.push("BELOW_MIN");
  if (tank.aboveSafeFill) flags.push("ABOVE_SAFE_FILL");
  return flags;
}

// ── Okuma tazeliği ───────────────────────────────────────────────────────────
/** Bu süreden eski son okuma "veri gelmiyor" uyarısı üretir (prob dakikalar mertebesinde okur). */
export const STALE_READING_MINUTES = 30;

export function minutesSince(iso: string, now: Date): number {
  const elapsed = now.getTime() - new Date(iso).getTime();
  return Math.max(0, Math.floor(elapsed / 60_000));
}

export function isStaleReading(minutes: number): boolean {
  return minutes > STALE_READING_MINUTES;
}

export interface ReadingAge {
  unit: "minute" | "hour" | "day";
  value: number;
}

/** "3 dk önce" / "2 sa önce" / "4 gün önce" için ölçek seçimi (metin çağıranda üretilir). */
export function readingAge(minutes: number): ReadingAge {
  if (minutes < 60) return { unit: "minute", value: minutes };
  if (minutes < 60 * 24) return { unit: "hour", value: Math.floor(minutes / 60) };
  return { unit: "day", value: Math.floor(minutes / (60 * 24)) };
}

// ── Seviye grafiği aralığı ───────────────────────────────────────────────────
export const TANK_RANGES = ["24h", "7d", "30d", "90d"] as const;
export type TankRange = (typeof TANK_RANGES)[number];
export type TankResolution = "RAW" | "HOURLY" | "DAILY";

const RANGE_HOURS: Record<TankRange, number> = { "24h": 24, "7d": 24 * 7, "30d": 24 * 30, "90d": 24 * 90 };

/**
 * Aralık → çözünürlük: 24 saat ham okumalarla (dolumun ani sıçraması görünsün), 7 gün saatlik,
 * 30 / 90 gün günlük. Ham okumada sunucu 5.000 noktayı aşarsa `range_too_large` döner; ekran
 * `fallbackResolution` ile saatliğe düşer ve kullanıcıya bildirir.
 */
export function resolutionForRange(range: TankRange): TankResolution {
  if (range === "24h") return "RAW";
  if (range === "7d") return "HOURLY";
  return "DAILY";
}

export function fallbackResolution(resolution: TankResolution): TankResolution | null {
  return resolution === "RAW" ? "HOURLY" : null;
}

export function rangeWindow(range: TankRange, now: Date): { from: string; to: string } {
  const to = new Date(now.getTime());
  const from = new Date(to.getTime() - RANGE_HOURS[range] * 3_600_000);
  return { from: from.toISOString(), to: to.toISOString() };
}
